// ---------------------------------------------------------------------------
// PB-T.14: Wave 2 Puck Components (20 most-needed new components)
// ---------------------------------------------------------------------------
// CSS variables only — no hardcoded colors. Responsive. Dark mode ready.
// ---------------------------------------------------------------------------

import { CONTENT_WIDTHS, FONT_SIZE_OPTION_VALUES, SPACING_OPTION_VALUES, spacing, spacingValue } from "@ease/page-builder";
import { createImageField } from "@/components/pages/puck-authoring-fields";

// ---------------------------------------------------------------------------
// 1. Section — Background section wrapper with image/gradient support
// ---------------------------------------------------------------------------
export const Section = {
  fields: {
    backgroundColor: { type: "text", label: "Background Color (CSS)" },
    backgroundImage: createImageField({ label: "Background image", helperText: "Optional background image for the section" }),
    padding: {
      type: "select",
      label: "Padding",
      options: [
        { label: "Small", value: "sm" },
        { label: "Medium", value: "md" },
        { label: "Large", value: "lg" },
        { label: "XL", value: "xl" },
      ],
    },
    maxWidth: { type: "text", label: "Max Width" },
    overlay: {
      type: "select",
      label: "Overlay",
      options: [
        { label: "None", value: "none" },
        { label: "Dark", value: "dark" },
        { label: "Light", value: "light" },
      ],
    },
  },
  defaultProps: {
    backgroundColor: "",
    backgroundImage: "",
    padding: "lg",
    maxWidth: CONTENT_WIDTHS.wide,
    overlay: "none",
  },
  render: ({ backgroundColor, backgroundImage, padding, maxWidth, overlay }: {
    backgroundColor: string; backgroundImage: string; padding: string; maxWidth: string; overlay: string;
  }) => {
    const padMap: Record<string, string> = { sm: spacing(3), md: spacing(6), lg: spacing(9), xl: spacing(12) };
    const overlayBg = overlay === "dark" ? "rgba(0,0,0,0.5)" : overlay === "light" ? "rgba(255,255,255,0.5)" : "transparent";
    return (
      <div style={{
        padding: padMap[padding] ?? "48px",
        background: backgroundImage ? `url(${backgroundImage}) center/cover no-repeat` : backgroundColor || "var(--color-surface)",
        position: "relative" as const,
      }}>
        {overlay !== "none" && (
          <div style={{ position: "absolute" as const, inset: 0, background: overlayBg }} />
        )}
        <div style={{ maxWidth, margin: "0 auto", position: "relative" as const }}>
          <p style={{ color: "var(--color-muted-foreground)", textAlign: "center" as const, fontSize: "0.875rem" }}>
            Section content — drop components inside
          </p>
        </div>
      </div>
    );
  },
};

// ---------------------------------------------------------------------------
// 2. RichText — Inline-editable rich text block
// ---------------------------------------------------------------------------
export const RichText = {
  fields: {
    content: { type: "textarea", label: "Content (Markdown-like)" },
    maxWidth: { type: "text", label: "Max Width" },
    fontSize: {
      type: "select",
      label: "Font Size",
      options: [
        { label: "Small", value: "sm" },
        { label: "Medium", value: "md" },
        { label: "Large", value: "lg" },
      ],
    },
  },
  defaultProps: {
    content: "## About Us\n\nWe help businesses grow with modern tools and **AI-powered automation**.\n\nOur platform is trusted by thousands of professionals worldwide.",
    maxWidth: CONTENT_WIDTHS.md,
    fontSize: "md",
  },
  render: ({ content, maxWidth, fontSize }: { content: string; maxWidth: string; fontSize: string }) => {
    const sizeMap: Record<string, string> = {
      sm: FONT_SIZE_OPTION_VALUES.sm,
      md: FONT_SIZE_OPTION_VALUES.md,
      lg: FONT_SIZE_OPTION_VALUES.lg,
    };
    const lines = content.split("\n").filter((l) => l.trim().length > 0);

    /** Render inline markdown: **bold** and *italic* */
    function renderInline(text: string, lineKey: number) {
      const parts: Array<{ type: "text" | "bold" | "italic"; content: string }> = [];
      let remaining = text;
      // Match **bold** first, then *italic*
      const regex = /\*\*(.+?)\*\*|\*(.+?)\*/g;
      let lastIndex = 0;
      let match: RegExpExecArray | null;
      while ((match = regex.exec(remaining)) !== null) {
        if (match.index > lastIndex) {
          parts.push({ type: "text", content: remaining.slice(lastIndex, match.index) });
        }
        if (match[1] !== undefined) {
          parts.push({ type: "bold", content: match[1] });
        } else if (match[2] !== undefined) {
          parts.push({ type: "italic", content: match[2] });
        }
        lastIndex = match.index + match[0].length;
      }
      if (lastIndex < remaining.length) {
        parts.push({ type: "text", content: remaining.slice(lastIndex) });
      }
      return (
        <>
          {parts.map((part, j) => {
            const key = `${lineKey}-${j}`;
            if (part.type === "bold") return <strong key={key}>{part.content}</strong>;
            if (part.type === "italic") return <em key={key}>{part.content}</em>;
            return <span key={key}>{part.content}</span>;
          })}
        </>
      );
    }

    return (
      <div style={{ maxWidth, margin: "0 auto", padding: `${spacing(4)} ${spacing(3)}`, fontSize: sizeMap[fontSize] ?? "1rem", lineHeight: 1.7, color: "var(--color-foreground)" }}>
        {lines.map((line, i) => {
          if (line.startsWith("## ")) return <h2 key={i} style={{ fontSize: "1.5rem", fontWeight: 700, marginBottom: "12px" }}>{renderInline(line.slice(3), i)}</h2>;
          if (line.startsWith("### ")) return <h3 key={i} style={{ fontSize: "1.25rem", fontWeight: 600, marginBottom: "8px" }}>{renderInline(line.slice(4), i)}</h3>;
          return <p key={i} style={{ marginBottom: "12px" }}>{renderInline(line, i)}</p>;
        })}
      </div>
    );
  },
};

// ---------------------------------------------------------------------------
// 3. FeatureCard — Icon + title + description card
// ---------------------------------------------------------------------------
export const FeatureCard = {
  fields: {
    items: { type: "textarea", label: "Features (one per line: Title | Description)" },
    columns: {
      type: "select",
      label: "Columns",
      options: [
        { label: "2", value: "2" },
        { label: "3", value: "3" },
        { label: "4", value: "4" },
      ],
    },
    variant: {
      type: "select",
      label: "Style",
      options: [
        { label: "Cards", value: "cards" },
        { label: "Minimal", value: "minimal" },
      ],
    },
  },
  defaultProps: {
    items: "AI Automation | Let AI handle the repetitive work so you can focus on what matters.\nCRM Built In | Every lead, every deal, every follow-up in one place.\nBooking System | Let clients book directly on your site with real-time availability.\nAnalytics | Know exactly what's working with clear, actionable dashboards.",
    columns: "2",
    variant: "cards",
  },
  render: ({ items, columns, variant }: { items: string; columns: string; variant: string }) => {
    const features = items.split("\n").filter(Boolean).map((line) => {
      const [title, desc] = line.split("|").map((s) => s.trim());
      return { title: title ?? "", desc: desc ?? "" };
    });
    const cols = Math.max(1, Math.min(Number(columns) || 2, 4));
    const isCards = variant === "cards";
    return (
      <div style={{ padding: `${spacing(6)} ${spacing(3)}`, maxWidth: CONTENT_WIDTHS.xl, margin: "0 auto" }}>
        <div style={{ display: "grid", gridTemplateColumns: `repeat(${cols}, 1fr)`, gap: spacing(3) }}>
          {features.map((f, i) => (
            <div key={i} style={{
              padding: isCards ? "24px" : "16px 0",
              borderRadius: isCards ? "12px" : "0",
              border: isCards ? "1px solid var(--color-border)" : "none",
              background: isCards ? "var(--color-card)" : "transparent",
            }}>
              <div style={{ width: "40px", height: "40px", borderRadius: "8px", background: "var(--color-primary)", opacity: 0.1, marginBottom: "12px" }} />
              <h3 style={{ fontSize: "1rem", fontWeight: 600, marginBottom: "8px", color: "var(--color-foreground)" }}>{f.title}</h3>
              <p style={{ fontSize: "0.875rem", lineHeight: 1.6, color: "var(--color-muted-foreground)" }}>{f.desc}</p>
            </div>
          ))}
        </div>
      </div>
    );
  },
};

// ---------------------------------------------------------------------------
// 4. IconText — Single icon + text row (for benefit lists)
// ---------------------------------------------------------------------------
export const IconText = {
  fields: {
    icon: { type: "text", label: "Icon (emoji or text)" },
    heading: { type: "text", label: "Heading" },
    description: { type: "textarea", label: "Description" },
    layout: {
      type: "select",
      label: "Layout",
      options: [
        { label: "Horizontal", value: "horizontal" },
        { label: "Vertical", value: "vertical" },
      ],
    },
  },
  defaultProps: {
    icon: "~",
    heading: "Lightning Fast Setup",
    description: "Get your website live in under 5 minutes. No coding required.",
    layout: "horizontal",
  },
  render: ({ icon, heading, description, layout }: { icon: string; heading: string; description: string; layout: string }) => {
    const isHorizontal = layout === "horizontal";
    return (
      <div style={{
        display: "flex",
        flexDirection: isHorizontal ? "row" as const : "column" as const,
        gap: spacing(2),
        alignItems: isHorizontal ? "flex-start" : "center",
        padding: spacing(3),
        textAlign: isHorizontal ? "left" as const : "center" as const,
      }}>
        <div style={{
          width: "48px", height: "48px", borderRadius: "12px",
          background: "var(--color-primary)", opacity: 0.9,
          display: "flex", alignItems: "center", justifyContent: "center",
          fontSize: "1.25rem", color: "white", flexShrink: 0,
        }}>
          {icon}
        </div>
        <div>
          <h3 style={{ fontWeight: 600, marginBottom: "4px", color: "var(--color-foreground)" }}>{heading}</h3>
          <p style={{ fontSize: "0.875rem", lineHeight: 1.6, color: "var(--color-muted-foreground)" }}>{description}</p>
        </div>
      </div>
    );
  },
};

// ---------------------------------------------------------------------------
// 5. Spacer — Vertical space between sections
// ---------------------------------------------------------------------------
export const Spacer = {
  fields: {
    height: {
      type: "select",
      label: "Height",
      options: [
        { label: "XS (16px)", value: "16" },
        { label: "SM (32px)", value: "32" },
        { label: "MD (48px)", value: "48" },
        { label: "LG (64px)", value: "64" },
        { label: "XL (96px)", value: "96" },
        { label: "2XL (128px)", value: "128" },
      ],
    },
  },
  defaultProps: { height: spacingValue(6) },
  render: ({ height }: { height: string }) => (
    <div style={{ height: `${height}px`, display: "flex", alignItems: "center", justifyContent: "center" }}>
      <span style={{ fontSize: "0.7rem", color: "var(--color-muted-foreground)", opacity: 0.5 }}>{height}px spacer</span>
    </div>
  ),
};

// ---------------------------------------------------------------------------
// 6. Divider — Horizontal line separator
// ---------------------------------------------------------------------------
export const Divider = {
  fields: {
    variant: {
      type: "select",
      label: "Style",
      options: [
        { label: "Solid", value: "solid" },
        { label: "Dashed", value: "dashed" },
        { label: "Dotted", value: "dotted" },
        { label: "Gradient", value: "gradient" },
      ],
    },
    width: { type: "text", label: "Width" },
    margin: { type: "text", label: "Vertical Margin" },
  },
  defaultProps: { variant: "solid", width: "100%", margin: SPACING_OPTION_VALUES.md },
  render: ({ variant, width, margin }: { variant: string; width: string; margin: string }) => (
    <div style={{ padding: `${margin} 24px`, display: "flex", justifyContent: "center" }}>
      {variant === "gradient" ? (
        <div style={{ height: "1px", width, background: "linear-gradient(to right, transparent, var(--color-border), transparent)" }} />
      ) : (
        <hr style={{ border: "none", borderTop: `1px ${variant} var(--color-border)`, width, margin: 0 }} />
      )}
    </div>
  ),
};

// ---------------------------------------------------------------------------
// 7. Accordion — Expandable FAQ/info sections
// ---------------------------------------------------------------------------
export const Accordion = {
  fields: {
    heading: { type: "text", label: "Heading" },
    items: { type: "textarea", label: "Items (one per line: Question | Answer)" },
    allowMultiple: {
      type: "select",
      label: "Allow Multiple Open",
      options: [
        { label: "Yes", value: "yes" },
        { label: "No", value: "no" },
      ],
    },
  },
  defaultProps: {
    heading: "Frequently Asked Questions",
    items: "How do I get started? | Sign up for free and follow the guided setup. You'll have your site live in under 5 minutes.\nIs there a free trial? | Yes! Every account starts with a 14-day free trial with full access to all features.\nCan I cancel anytime? | Absolutely. No contracts, no commitments. Cancel from your dashboard in one click.\nDo you offer custom domains? | Yes. Connect your own domain or purchase one directly through the platform.",
    allowMultiple: "no",
  },
  render: ({ heading, items }: { heading: string; items: string }) => {
    const faqs = items.split("\n").filter(Boolean).map((line) => {
      const [q, a] = line.split("|").map((s) => s.trim());
      return { question: q ?? "", answer: a ?? "" };
    });
    return (
      <div style={{ padding: `${SPACING_OPTION_VALUES.lg}px ${SPACING_OPTION_VALUES.sm}px`, maxWidth: CONTENT_WIDTHS.md, margin: "0 auto" }}>
        {heading && <h2 style={{ textAlign: "center" as const, marginBottom: `${SPACING_OPTION_VALUES.md}px`, color: "var(--color-foreground)" }}>{heading}</h2>}
        {faqs.map((faq, i) => (
          <div key={i} style={{ borderBottom: "1px solid var(--color-border)", padding: "16px 0" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", cursor: "pointer" }}>
              <h3 style={{ fontWeight: 500, color: "var(--color-foreground)", fontSize: "0.95rem" }}>{faq.question}</h3>
              <span style={{ color: "var(--color-muted-foreground)", fontSize: "1.25rem", flexShrink: 0, marginLeft: "16px" }}>+</span>
            </div>
            {i === 0 && (
              <p style={{ marginTop: "12px", fontSize: "0.875rem", lineHeight: 1.6, color: "var(--color-muted-foreground)" }}>{faq.answer}</p>
            )}
          </div>
        ))}
      </div>
    );
  },
};

// ---------------------------------------------------------------------------
// 8. Footer — Site footer with columns
// ---------------------------------------------------------------------------
export const Footer = {
  fields: {
    companyName: { type: "text", label: "Company Name" },
    tagline: { type: "text", label: "Tagline" },
    columns: { type: "textarea", label: "Columns (format: Title\\nLink | URL\\nLink | URL\\n---\\nTitle\\n...)" },
    copyright: { type: "text", label: "Copyright Text" },
  },
  defaultProps: {
    companyName: "Your Company",
    tagline: "Making business easier.",
    columns: "Product\nFeatures | #features\nPricing | #pricing\nTemplates | #templates\n---\nCompany\nAbout | /about\nBlog | /blog\nCareers | /careers\n---\nSupport\nHelp Center | /help\nContact | /contact\nStatus | /status",
    copyright: "2026 Your Company. All rights reserved.",
  },
  render: ({ companyName, tagline, columns, copyright }: { companyName: string; tagline: string; columns: string; copyright: string }) => {
    const sections = columns.split("---").map((section) => {
      const lines = section.trim().split("\n").filter(Boolean);
      const title = lines[0] ?? "";
      const links = lines.slice(1).map((l) => {
        const [label, url] = l.split("|").map((s) => s.trim());
        return { label: label ?? "", url: url ?? "#" };
      });
      return { title, links };
    });
    return (
      <footer style={{ background: "var(--color-card)", borderTop: "1px solid var(--color-border)", padding: `${SPACING_OPTION_VALUES.lg}px ${SPACING_OPTION_VALUES.sm}px ${SPACING_OPTION_VALUES.sm}px` }}>
        <div style={{ maxWidth: CONTENT_WIDTHS.xl, margin: "0 auto", display: "flex", flexWrap: "wrap" as const, gap: `${SPACING_OPTION_VALUES.lg}px` }}>
          <div style={{ flex: "1 1 250px" }}>
            <h3 style={{ fontWeight: 700, color: "var(--color-foreground)", marginBottom: "8px" }}>{companyName}</h3>
            <p style={{ fontSize: "0.875rem", color: "var(--color-muted-foreground)" }}>{tagline}</p>
          </div>
          {sections.map((s, i) => (
            <div key={i} style={{ flex: "0 0 150px" }}>
              <h4 style={{ fontWeight: 600, fontSize: "0.875rem", color: "var(--color-foreground)", marginBottom: "12px" }}>{s.title}</h4>
              {s.links.map((link, j) => (
                <p key={j} style={{ fontSize: "0.85rem", color: "var(--color-muted-foreground)", margin: "6px 0", cursor: "pointer" }}>{link.label}</p>
              ))}
            </div>
          ))}
        </div>
        <div style={{ maxWidth: CONTENT_WIDTHS.xl, margin: `${SPACING_OPTION_VALUES.md}px auto 0`, paddingTop: `${SPACING_OPTION_VALUES.sm}px`, borderTop: "1px solid var(--color-border)", textAlign: "center" as const }}>
          <p style={{ fontSize: "0.8rem", color: "var(--color-muted-foreground)" }}>{copyright}</p>
        </div>
      </footer>
    );
  },
};

// ---------------------------------------------------------------------------
// 9. NotificationBar — Top-of-page announcement banner
// ---------------------------------------------------------------------------
export const NotificationBar = {
  fields: {
    message: { type: "text", label: "Message" },
    linkText: { type: "text", label: "Link Text" },
    linkUrl: { type: "text", label: "Link URL" },
    variant: {
      type: "select",
      label: "Variant",
      options: [
        { label: "Info", value: "info" },
        { label: "Success", value: "success" },
        { label: "Warning", value: "warning" },
        { label: "Promo", value: "promo" },
      ],
    },
    dismissible: {
      type: "select",
      label: "Dismissible",
      options: [
        { label: "Yes", value: "yes" },
        { label: "No", value: "no" },
      ],
    },
  },
  defaultProps: {
    message: "New: AI-powered page generation is here!",
    linkText: "Try it now",
    linkUrl: "#",
    variant: "promo",
    dismissible: "yes",
  },
  render: ({ message, linkText, variant, dismissible }: { message: string; linkText: string; variant: string; dismissible: string }) => {
    const variantStyles: Record<string, { bg: string; text: string }> = {
      info: { bg: "var(--color-primary)", text: "white" },
      success: { bg: "var(--color-success, #22c55e)", text: "white" },
      warning: { bg: "var(--color-warning, #f59e0b)", text: "white" },
      promo: { bg: "linear-gradient(to right, var(--color-primary), var(--color-ai, #7c3aed))", text: "white" },
    };
    const vstyle = variantStyles[variant] ?? { bg: "var(--color-primary)", text: "white" };
    return (
      <div style={{ padding: `${spacing(1)} ${spacing(3)}`, background: vstyle.bg, color: vstyle.text, display: "flex", alignItems: "center", justifyContent: "center", gap: spacing(2), fontSize: "0.875rem" }}>
        <span>{message}</span>
        {linkText && <span style={{ textDecoration: "underline", cursor: "pointer", fontWeight: 600 }}>{linkText}</span>}
        {dismissible === "yes" && <span style={{ marginLeft: "auto", cursor: "pointer", opacity: 0.7 }}>x</span>}
      </div>
    );
  },
};

// ---------------------------------------------------------------------------
// 10. BackToTop — Scroll-to-top button
// ---------------------------------------------------------------------------
export const BackToTop = {
  fields: {
    label: { type: "text", label: "Label" },
    showAfterPx: { type: "number", label: "Show After Scroll (px)" },
    position: {
      type: "select",
      label: "Position",
      options: [
        { label: "Bottom Right", value: "br" },
        { label: "Bottom Left", value: "bl" },
      ],
    },
  },
  defaultProps: { label: "", showAfterPx: 300, position: "br" },
  render: ({ label, position }: { label: string; position: string }) => (
    <div style={{
      display: "flex", justifyContent: position === "bl" ? "flex-start" : "flex-end",
      padding: spacing(3),
    }}>
      <button style={{
        width: label ? "auto" : "44px", height: "44px",
        borderRadius: label ? "22px" : "50%",
        background: "var(--color-primary)", color: "white", border: "none",
        display: "flex", alignItems: "center", justifyContent: "center", gap: "6px",
        padding: label ? "0 16px" : "0",
        cursor: "pointer", boxShadow: "var(--shadow-md, 0 4px 6px rgba(0,0,0,0.1))",
        fontSize: "0.875rem", fontWeight: 600,
      }}>
        <span style={{ fontSize: "1rem" }}>^</span>
        {label && <span>{label}</span>}
      </button>
    </div>
  ),
};

// ---------------------------------------------------------------------------
// 11. SocialIcons — Row of social media links
// ---------------------------------------------------------------------------
export const SocialIcons = {
  fields: {
    icons: { type: "textarea", label: "Icons (one per line: Platform | URL)" },
    size: {
      type: "select",
      label: "Size",
      options: [
        { label: "Small", value: "sm" },
        { label: "Medium", value: "md" },
        { label: "Large", value: "lg" },
      ],
    },
    style: {
      type: "select",
      label: "Style",
      options: [
        { label: "Filled", value: "filled" },
        { label: "Outline", value: "outline" },
        { label: "Plain", value: "plain" },
      ],
    },
  },
  defaultProps: {
    icons: "Facebook | #\nInstagram | #\nTwitter | #\nLinkedIn | #\nYouTube | #",
    size: "md",
    style: "filled",
  },
  render: ({ icons, size, style: iconStyle }: { icons: string; size: string; style: string }) => {
    const parsed = icons.split("\n").filter(Boolean).map((line) => {
      const [platform] = line.split("|").map((s) => s.trim());
      return { platform: platform ?? "" };
    });
    const sizeMap: Record<string, string> = { sm: "32px", md: "40px", lg: "48px" };
    const sz = sizeMap[size] ?? "40px";
    return (
      <div style={{ display: "flex", gap: spacing(2), justifyContent: "center", padding: spacing(3), flexWrap: "wrap" as const }}>
        {parsed.map((item, i) => (
          <div key={i} style={{
            width: sz, height: sz, borderRadius: "8px",
            display: "flex", alignItems: "center", justifyContent: "center",
            fontSize: "0.7rem", fontWeight: 600, cursor: "pointer",
            ...(iconStyle === "filled"
              ? { background: "var(--color-primary)", color: "white" }
              : iconStyle === "outline"
                ? { border: "1px solid var(--color-border)", color: "var(--color-foreground)", background: "transparent" }
                : { color: "var(--color-muted-foreground)", background: "transparent" }),
          }}>
            {item.platform.slice(0, 2).toUpperCase()}
          </div>
        ))}
      </div>
    );
  },
};

// ---------------------------------------------------------------------------
// 12. ImageEnhanced — Image with caption, lightbox, and aspect ratio
// ---------------------------------------------------------------------------
export const ImageEnhanced = {
  fields: {
    src: createImageField({ label: "Image" }),
    alt: { type: "text", label: "Alt Text" },
    caption: { type: "text", label: "Caption" },
    aspectRatio: {
      type: "select",
      label: "Aspect Ratio",
      options: [
        { label: "Auto", value: "auto" },
        { label: "16:9", value: "16/9" },
        { label: "4:3", value: "4/3" },
        { label: "1:1", value: "1/1" },
        { label: "3:2", value: "3/2" },
      ],
    },
    borderRadius: { type: "text", label: "Border Radius" },
  },
  defaultProps: {
    src: "",
    alt: "Image description",
    caption: "",
    aspectRatio: "16/9",
    borderRadius: "12px",
  },
  render: ({ src, alt, caption, aspectRatio, borderRadius }: { src: string; alt: string; caption: string; aspectRatio: string; borderRadius: string }) => (
    <figure style={{ padding: spacing(3), margin: 0 }}>
      {src ? (
        <img
          src={src}
          alt={alt}
          style={{
            width: "100%", borderRadius, objectFit: "cover" as const,
            ...(aspectRatio !== "auto" ? { aspectRatio } : {}),
          }}
        />
      ) : (
        <div style={{
          width: "100%", borderRadius, background: "var(--color-muted)",
          display: "flex", alignItems: "center", justifyContent: "center",
          color: "var(--color-muted-foreground)", fontSize: "0.875rem",
          ...(aspectRatio !== "auto" ? { aspectRatio } : { height: "200px" }),
        }}>
          Choose or upload an image
        </div>
      )}
      {caption && (
        <figcaption style={{ textAlign: "center" as const, marginTop: "8px", fontSize: "0.85rem", color: "var(--color-muted-foreground)" }}>
          {caption}
        </figcaption>
      )}
    </figure>
  ),
};

// ---------------------------------------------------------------------------
// 13. OfferCard — Limited-time offer with badge and CTA
// ---------------------------------------------------------------------------
export const OfferCard = {
  fields: {
    badge: { type: "text", label: "Badge Text" },
    heading: { type: "text", label: "Heading" },
    description: { type: "textarea", label: "Description" },
    originalPrice: { type: "text", label: "Original Price" },
    offerPrice: { type: "text", label: "Offer Price" },
    ctaText: { type: "text", label: "CTA Text" },
    ctaUrl: { type: "text", label: "CTA URL" },
  },
  defaultProps: {
    badge: "Limited Time",
    heading: "Spring Launch Special",
    description: "Get started with our Growth plan at a fraction of the cost. Includes all AI features, 5,000 contacts, and priority support.",
    originalPrice: "€89/mo",
    offerPrice: "€49/mo",
    ctaText: "Claim This Offer",
    ctaUrl: "#",
  },
  render: ({ badge, heading, description, originalPrice, offerPrice, ctaText }: {
    badge: string; heading: string; description: string; originalPrice: string; offerPrice: string; ctaText: string;
  }) => (
    <div style={{ padding: `${spacing(4)} ${spacing(3)}`, maxWidth: CONTENT_WIDTHS.xs, margin: "0 auto" }}>
      <div style={{ border: "2px solid var(--color-primary)", borderRadius: "16px", padding: "32px", textAlign: "center" as const, position: "relative" as const }}>
        {badge && (
          <span style={{
            position: "absolute" as const, top: "-12px", left: "50%", transform: "translateX(-50%)",
            background: "var(--color-primary)", color: "white", padding: "4px 16px",
            borderRadius: "20px", fontSize: "0.75rem", fontWeight: 700, textTransform: "uppercase" as const,
          }}>
            {badge}
          </span>
        )}
        <h3 style={{ fontSize: "1.5rem", fontWeight: 700, marginBottom: "12px", marginTop: "8px", color: "var(--color-foreground)" }}>{heading}</h3>
        <p style={{ fontSize: "0.9rem", color: "var(--color-muted-foreground)", marginBottom: "20px", lineHeight: 1.6 }}>{description}</p>
        <div style={{ marginBottom: "20px" }}>
          {originalPrice && <span style={{ textDecoration: "line-through", color: "var(--color-muted-foreground)", marginRight: "8px" }}>{originalPrice}</span>}
          <span style={{ fontSize: "2rem", fontWeight: 800, color: "var(--color-primary)" }}>{offerPrice}</span>
        </div>
        <button style={{ padding: "14px 32px", background: "var(--color-primary)", color: "white", border: "none", borderRadius: "8px", fontWeight: 700, cursor: "pointer", fontSize: "1rem", width: "100%" }}>
          {ctaText}
        </button>
      </div>
    </div>
  ),
};

// ---------------------------------------------------------------------------
// 14. ServiceMenu — List of services with prices
// ---------------------------------------------------------------------------
export const ServiceMenu = {
  fields: {
    heading: { type: "text", label: "Heading" },
    items: { type: "textarea", label: "Services (one per line: Name | Price | Duration)" },
    showBooking: {
      type: "select",
      label: "Show Book Button",
      options: [
        { label: "Yes", value: "yes" },
        { label: "No", value: "no" },
      ],
    },
  },
  defaultProps: {
    heading: "Our Services",
    items: "Deep Tissue Massage | €85 | 60 min\nSwedish Massage | €75 | 60 min\nHot Stone Therapy | €95 | 75 min\nAromatherapy | €80 | 60 min\nCouples Massage | €150 | 90 min",
    showBooking: "yes",
  },
  render: ({ heading, items, showBooking }: { heading: string; items: string; showBooking: string }) => {
    const services = items.split("\n").filter(Boolean).map((line) => {
      const parts = line.split("|").map((s) => s.trim());
      return { name: parts[0] ?? "", price: parts[1] ?? "", duration: parts[2] ?? "" };
    });
    return (
      <div style={{ padding: `${spacing(6)} ${spacing(3)}`, maxWidth: CONTENT_WIDTHS.sm, margin: "0 auto" }}>
        {heading && <h2 style={{ textAlign: "center" as const, marginBottom: "32px", color: "var(--color-foreground)" }}>{heading}</h2>}
        {services.map((s, i) => (
          <div key={i} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "16px 0", borderBottom: "1px solid var(--color-border)" }}>
            <div>
              <h3 style={{ fontWeight: 500, color: "var(--color-foreground)", fontSize: "0.95rem" }}>{s.name}</h3>
              {s.duration && <span style={{ fontSize: "0.8rem", color: "var(--color-muted-foreground)" }}>{s.duration}</span>}
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
              <span style={{ fontWeight: 700, color: "var(--color-foreground)" }}>{s.price}</span>
              {showBooking === "yes" && (
                <button style={{ padding: "6px 14px", border: "1px solid var(--color-primary)", color: "var(--color-primary)", background: "transparent", borderRadius: "6px", fontSize: "0.8rem", fontWeight: 600, cursor: "pointer" }}>
                  Book
                </button>
              )}
            </div>
          </div>
        ))}
      </div>
    );
  },
};

// ---------------------------------------------------------------------------
// 15. ChatWidget — Floating chat bubble preview
// ---------------------------------------------------------------------------
export const ChatWidget = {
  fields: {
    greeting: { type: "text", label: "Greeting Message" },
    placeholder: { type: "text", label: "Input Placeholder" },
    offlineMessage: { type: "text", label: "Offline Message" },
    collectName: {
      type: "select",
      label: "Ask Name First",
      options: [
        { label: "Yes", value: "yes" },
        { label: "No", value: "no" },
      ],
    },
    position: {
      type: "select",
      label: "Position",
      options: [
        { label: "Bottom Right", value: "br" },
        { label: "Bottom Left", value: "bl" },
      ],
    },
    aiEnabled: {
      type: "select",
      label: "AI Auto-Reply",
      options: [
        { label: "Yes", value: "yes" },
        { label: "No", value: "no" },
      ],
    },
  },
  defaultProps: {
    greeting: "Hi there! How can we help you today?",
    placeholder: "Type a message...",
    offlineMessage: "We'll get back to you soon.",
    collectName: "yes",
    position: "br",
    aiEnabled: "yes",
  },
  render: ({ greeting, placeholder, position, aiEnabled, collectName }: { greeting: string; placeholder: string; position: string; aiEnabled: string; collectName: string }) => (
    <div style={{ display: "flex", justifyContent: position === "bl" ? "flex-start" : "flex-end", padding: spacing(3) }}>
      <div style={{ width: "320px", borderRadius: "16px", border: "1px solid var(--color-border)", overflow: "hidden", boxShadow: "var(--shadow-lg, 0 10px 15px rgba(0,0,0,0.1))" }}>
        <div style={{ background: "var(--color-primary)", color: "white", padding: "16px" }}>
          <p style={{ fontWeight: 600, fontSize: "0.9rem" }}>Chat with us</p>
          {aiEnabled === "yes" && <p style={{ fontSize: "0.75rem", opacity: 0.8, marginTop: "2px" }}>AI-powered replies</p>}
        </div>
        <div style={{ padding: "16px", background: "var(--color-background)", minHeight: "120px" }}>
          <div style={{ background: "var(--color-muted)", borderRadius: "12px 12px 12px 4px", padding: "10px 14px", maxWidth: "80%", fontSize: "0.85rem", color: "var(--color-foreground)" }}>
            {greeting}
          </div>
        </div>
        {collectName === "yes" ? (
          <div style={{ padding: "12px", borderTop: "1px solid var(--color-border)" }}>
            <p style={{ fontSize: "0.8rem", color: "var(--color-muted-foreground)", marginBottom: "8px" }}>What's your name?</p>
            <div style={{ display: "flex", gap: "8px" }}>
              <input placeholder="Your name" style={{ flex: 1, border: "1px solid var(--color-border)", borderRadius: "20px", padding: "8px 14px", fontSize: "0.85rem" }} />
              <button style={{ padding: "8px 14px", borderRadius: "20px", background: "var(--color-primary)", color: "white", border: "none", fontWeight: 600, cursor: "pointer", fontSize: "0.85rem" }}>Start</button>
            </div>
          </div>
        ) : (
          <div style={{ padding: "12px", borderTop: "1px solid var(--color-border)", display: "flex", gap: "8px" }}>
            <input placeholder={placeholder} style={{ flex: 1, border: "1px solid var(--color-border)", borderRadius: "20px", padding: "8px 14px", fontSize: "0.85rem" }} />
            <button style={{ width: "36px", height: "36px", borderRadius: "50%", background: "var(--color-primary)", color: "white", border: "none", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>
              &gt;
            </button>
          </div>
        )}
      </div>
    </div>
  ),
};

// ---------------------------------------------------------------------------
// 16. FloatingCta — Persistent floating action button
// ---------------------------------------------------------------------------
export const FloatingCta = {
  fields: {
    text: { type: "text", label: "Button Text" },
    url: { type: "text", label: "URL" },
    position: {
      type: "select",
      label: "Position",
      options: [
        { label: "Bottom Right", value: "br" },
        { label: "Bottom Left", value: "bl" },
        { label: "Bottom Center", value: "bc" },
      ],
    },
    variant: {
      type: "select",
      label: "Style",
      options: [
        { label: "Primary", value: "primary" },
        { label: "Accent", value: "accent" },
      ],
    },
  },
  defaultProps: { text: "Book Now", url: "#booking", position: "br", variant: "primary" },
  render: ({ text, position, variant }: { text: string; position: string; variant: string }) => {
    const justifyMap: Record<string, string> = { bl: "flex-start", br: "flex-end", bc: "center" };
    const bg = variant === "accent" ? "var(--color-ai, #7c3aed)" : "var(--color-primary)";
    return (
      <div style={{ display: "flex", justifyContent: justifyMap[position] ?? "flex-end", padding: spacing(3) }}>
        <button style={{
          padding: "14px 28px", background: bg, color: "white", border: "none",
          borderRadius: "28px", fontWeight: 700, cursor: "pointer", fontSize: "0.95rem",
          boxShadow: "var(--shadow-lg, 0 10px 15px rgba(0,0,0,0.1))",
        }}>
          {text}
        </button>
      </div>
    );
  },
};

// ---------------------------------------------------------------------------
// 17. ScrollTriggeredCta — CTA that appears on scroll
// ---------------------------------------------------------------------------
export const ScrollTriggeredCta = {
  fields: {
    heading: { type: "text", label: "Heading" },
    ctaText: { type: "text", label: "CTA Text" },
    ctaUrl: { type: "text", label: "CTA URL" },
    triggerPercent: { type: "number", label: "Trigger at Scroll %" },
    position: {
      type: "select",
      label: "Position",
      options: [
        { label: "Bottom Bar", value: "bar" },
        { label: "Slide In Right", value: "slide" },
      ],
    },
  },
  defaultProps: {
    heading: "Ready to get started?",
    ctaText: "Start Free Trial",
    ctaUrl: "#",
    triggerPercent: 50,
    position: "bar",
  },
  render: ({ heading, ctaText, triggerPercent, position }: { heading: string; ctaText: string; triggerPercent: number; position: string }) => (
      <div style={{
      padding: `${spacing(2)} ${spacing(3)}`,
      background: position === "bar" ? "var(--color-card)" : "var(--color-background)",
      border: "1px solid var(--color-border)",
      borderRadius: position === "slide" ? "12px" : "0",
      display: "flex", alignItems: "center", justifyContent: "space-between", gap: "16px",
      maxWidth: position === "slide" ? "360px" : "none",
      boxShadow: "var(--shadow-md, 0 4px 6px rgba(0,0,0,0.1))",
    }}>
      <div>
        <p style={{ fontWeight: 600, color: "var(--color-foreground)", fontSize: "0.9rem" }}>{heading}</p>
        <p style={{ fontSize: "0.75rem", color: "var(--color-muted-foreground)" }}>Appears at {triggerPercent}% scroll</p>
      </div>
      <button style={{ padding: "10px 20px", background: "var(--color-primary)", color: "white", border: "none", borderRadius: "6px", fontWeight: 600, cursor: "pointer", fontSize: "0.85rem", whiteSpace: "nowrap" as const }}>
        {ctaText}
      </button>
    </div>
  ),
};

// ---------------------------------------------------------------------------
// 18. Breadcrumb — Navigation breadcrumb trail
// ---------------------------------------------------------------------------
export const Breadcrumb = {
  fields: {
    items: { type: "textarea", label: "Items (one per line: Label | URL)" },
    separator: { type: "text", label: "Separator" },
  },
  defaultProps: {
    items: "Home | /\nServices | /services\nMassage Therapy | /services/massage",
    separator: "/",
  },
  render: ({ items, separator }: { items: string; separator: string }) => {
    const crumbs = items.split("\n").filter(Boolean).map((line) => {
      const [label, url] = line.split("|").map((s) => s.trim());
      return { label: label ?? "", url: url ?? "#" };
    });
    return (
      <nav style={{ padding: `${spacing(2)} ${spacing(3)}` }}>
        <ol style={{ display: "flex", gap: "8px", listStyle: "none", margin: 0, padding: 0, fontSize: "0.85rem" }}>
          {crumbs.map((crumb, i) => (
            <li key={i} style={{ display: "flex", alignItems: "center", gap: "8px" }}>
              {i > 0 && <span style={{ color: "var(--color-muted-foreground)" }}>{separator}</span>}
              <span style={{
                color: i === crumbs.length - 1 ? "var(--color-foreground)" : "var(--color-primary)",
                fontWeight: i === crumbs.length - 1 ? 500 : 400,
                cursor: i < crumbs.length - 1 ? "pointer" : "default",
              }}>
                {crumb.label}
              </span>
            </li>
          ))}
        </ol>
      </nav>
    );
  },
};

// ---------------------------------------------------------------------------
// 19. AiRecommendation — AI-powered content suggestion card
// ---------------------------------------------------------------------------
export const AiRecommendation = {
  fields: {
    heading: { type: "text", label: "Heading" },
    description: { type: "textarea", label: "Description" },
    ctaText: { type: "text", label: "CTA Text" },
    ctaUrl: { type: "text", label: "CTA URL" },
    dataSource: {
      type: "select",
      label: "Data Source",
      options: [
        { label: "Browsing History", value: "history" },
        { label: "Similar Users", value: "similar" },
        { label: "Popular", value: "popular" },
      ],
    },
  },
  defaultProps: {
    heading: "Recommended for You",
    description: "Based on your interests, we think you'll love these services.",
    ctaText: "View All",
    ctaUrl: "#",
    dataSource: "history",
  },
  render: ({ heading, description, ctaText, dataSource }: { heading: string; description: string; ctaText: string; dataSource: string }) => (
    <div style={{ padding: `${spacing(4)} ${spacing(3)}`, maxWidth: CONTENT_WIDTHS.lg, margin: "0 auto" }}>
      <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "8px" }}>
        <span style={{ fontSize: "0.7rem", padding: "2px 8px", borderRadius: "4px", background: "var(--color-ai, #7c3aed)", color: "white", fontWeight: 600 }}>
          AI
        </span>
        <h2 style={{ fontSize: "1.25rem", fontWeight: 600, color: "var(--color-foreground)" }}>{heading}</h2>
      </div>
      <p style={{ fontSize: "0.875rem", color: "var(--color-muted-foreground)", marginBottom: "20px" }}>
        {description} <span style={{ fontSize: "0.75rem" }}>({dataSource})</span>
      </p>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: "16px" }}>
        {[1, 2, 3].map((i) => (
          <div key={i} style={{ border: "1px solid var(--color-border)", borderRadius: "12px", padding: "16px", background: "var(--color-card)" }}>
            <div style={{ width: "100%", aspectRatio: "16/9", background: "var(--color-muted)", borderRadius: "8px", marginBottom: "12px" }} />
            <div style={{ height: "12px", width: "70%", background: "var(--color-muted)", borderRadius: "4px", marginBottom: "6px" }} />
            <div style={{ height: "10px", width: "50%", background: "var(--color-muted)", borderRadius: "4px" }} />
          </div>
        ))}
      </div>
      <div style={{ textAlign: "center" as const, marginTop: "20px" }}>
        <button style={{ padding: "8px 20px", border: "1px solid var(--color-primary)", color: "var(--color-primary)", background: "transparent", borderRadius: "6px", fontWeight: 600, cursor: "pointer", fontSize: "0.85rem" }}>
          {ctaText}
        </button>
      </div>
    </div>
  ),
};

// ---------------------------------------------------------------------------
// 20. MapEmbed — Location map with address
// ---------------------------------------------------------------------------
export const MapEmbed = {
  fields: {
    address: { type: "text", label: "Address" },
    heading: { type: "text", label: "Heading" },
    height: { type: "text", label: "Height" },
    showAddress: {
      type: "select",
      label: "Show Address Below",
      options: [
        { label: "Yes", value: "yes" },
        { label: "No", value: "no" },
      ],
    },
  },
  defaultProps: {
    address: "Amsterdam, Netherlands",
    heading: "Find Us",
    height: "300px",
    showAddress: "yes",
  },
  render: ({ address, heading, height, showAddress }: { address: string; heading: string; height: string; showAddress: string }) => (
    <div style={{ padding: `${spacing(4)} ${spacing(3)}` }}>
      {heading && <h2 style={{ textAlign: "center" as const, marginBottom: "20px", color: "var(--color-foreground)" }}>{heading}</h2>}
      <div style={{
        width: "100%", height, borderRadius: "12px", overflow: "hidden",
        border: "1px solid var(--color-border)", background: "var(--color-muted)",
        display: "flex", alignItems: "center", justifyContent: "center",
      }}>
        <div style={{ textAlign: "center" as const, color: "var(--color-muted-foreground)" }}>
          <div style={{ fontSize: "2rem", marginBottom: "8px" }}>&#x1F4CD;</div>
          <p style={{ fontSize: "0.875rem" }}>Map: {address}</p>
          <p style={{ fontSize: "0.75rem", marginTop: "4px" }}>Embedded map renders on published page</p>
        </div>
      </div>
      {showAddress === "yes" && (
        <p style={{ textAlign: "center" as const, marginTop: "12px", fontSize: "0.9rem", color: "var(--color-foreground)" }}>{address}</p>
      )}
    </div>
  ),
};
